/**
 * What a field device prefetches for offline use (migration plan P2-05).
 *
 * The app-mount prefetch (hooks/useOfflineCache.ts) used to copy the WHOLE
 * tenant's contact directory and personnel credential roster (names, phones,
 * emails, photos, qualifications) onto every device, including responders'
 * phones. No field screen reads either cache: nothing reads cachedCredentials
 * at all, and cachedContacts backs the console's Contacts page and the ICS 214
 * roster picker, which fill it themselves when opened. On a lost or seized
 * phone that copy is the largest store of other people's personal data.
 *
 * Field devices (the native shell, or the /field routes) therefore fetch only
 * what the field screens need, and drop what earlier builds prefetched, once.
 * The console browser keeps its prefetch unchanged.
 */
import { db } from './db';
import { photoCacheService } from './photo-cache.service';
import { isFieldClient } from '@/services/client-mode';

export const DIRECTORY_PURGE_FLAG = 'cb.p205.fieldDirectoryPurged';

/** True when the tenant-wide contact and credential prefetch may run. */
export function prefetchesDirectory(): boolean {
  return !isFieldClient();
}

function purgedAlready(): boolean {
  try {
    return localStorage.getItem(DIRECTORY_PURGE_FLAG) === '1';
  } catch {
    return false;
  }
}

function markPurged(): void {
  try {
    localStorage.setItem(DIRECTORY_PURGE_FLAG, '1');
  } catch {
    // Storage unavailable: the purge simply runs again next time (idempotent).
  }
}

/**
 * On a field device, delete the tenant-wide contact and credential copies an
 * earlier build prefetched. Runs once per install; a Contacts page the user
 * opens afterwards refills its own cache as before.
 */
export async function purgePrefetchedDirectoryOnFieldDevice(): Promise<boolean> {
  if (prefetchesDirectory() || purgedAlready()) return false;
  await db.cachedCredentials.clear();
  await db.cachedContacts.clear();
  await photoCacheService.clearCache('credential').catch(() => undefined);
  await photoCacheService.clearCache('contact').catch(() => undefined);
  markPurged();
  return true;
}
